/**
 * FeedCard — the single shared post card used by the main feed, the
 * community/org feeds, and the Project Journal. Each surface maps its own data
 * into `FeedCardModel` (client-side adapters) and supplies its own menu actions
 * and open handler, so the card renders identically everywhere and is
 * maintained in one place.
 *
 * Owns: the photo→theater viewer (adults only), the "..." menu, and the shared
 * FeedActionBar (react / comment / share). The newest main-feed behavior is the
 * reference; this is that card, generalized.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { mediaSrc } from '../../api/mediaUrl';
import FeedImage from '../FeedImage/FeedImage';
import { sanitizeEmbed, sanitizeRichText } from '../../utils/sanitizeHtml';
import { FeedActionBar } from '../Comments/FeedActionBar';
import { ConversationModal } from '../Comments/ConversationModal';
import styles from './FeedCard.module.css';

export interface FeedCardMenuAction {
  key: string;
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

export interface FeedCardModel {
  id: string;
  /** Pillar key for the start-edge accent (problem/solution/enabler/community/project). */
  pillar?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  authorHref?: string | null;
  /** "posted in {community}" affordance (optional). */
  postedInName?: string | null;
  postedInHref?: string | null;
  /** Preformatted relative/absolute time string. */
  dateText?: string | null;
  /** Short uppercase type badge (optional). */
  badgeText?: string | null;
  embedHtml?: string | null;
  embedType?: string | null;
  /** Already-resolved image src (adapter applies mediaSrc / fileUrl as needed). */
  imageSrc?: string | null;
  /** Raw image url for the theater (falls back to imageSrc). */
  theaterImageUrl?: string | null;
  title?: string | null;
  body?: string | null;
  /** Rich-text body (sanitized here) — e.g. Journal status narratives. Takes precedence over `body`. */
  bodyHtml?: string | null;
  /** "Shared by X" line (community feed). */
  sharerText?: string | null;
  /** External destination for the theater "Open link" affordance. */
  linkUrl?: string | null;
  /** Comment anchor — drives the action bar + theater comments. */
  anchorType?: string | null;
  anchorId?: string | null;
  /** Primary click-through (card body). */
  onOpen?: () => void;
  /** Navigate to the originating community/pillar (bottom CTA). */
  ctaLabel?: string | null;
  onCta?: () => void;
  /** Navigate to the author profile/community (avatar + name). */
  onAuthor?: () => void;
  menuActions?: FeedCardMenuAction[];
}

const PILLAR_VAR: Record<string, string> = {
  problem: 'var(--pillar-problems)',
  solution: 'var(--pillar-solutions)',
  enabler: 'var(--pillar-enabler)',
  community: 'var(--pillar-community)',
  project: 'var(--pillar-projects)',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '·';
}

interface Props {
  model: FeedCardModel;
  /** Whether to show comments/reactions + the photo theater. The open social
   *  feed gates this on adulthood; project surfaces (Journal) pass true. */
  enableComments: boolean;
}

export const FeedCard: React.FC<Props> = ({ model, enableComments }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [theaterOpen, setTheaterOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const anchor = model.anchorType && model.anchorId
    ? { anchorType: model.anchorType, anchorId: model.anchorId }
    : null;
  const canTheater = enableComments && !!anchor && !!model.imageSrc;

  const onImageClick = useCallback((e: React.MouseEvent) => {
    if (canTheater) { e.stopPropagation(); setTheaterOpen(true); }
    else model.onOpen?.();
  }, [canTheater, model]);

  const accent = model.pillar ? PILLAR_VAR[model.pillar] : undefined;
  const menu = model.menuActions ?? [];

  return (
    <div
      className={`${styles.card} ${model.onOpen ? styles.clickable : ''}`}
      style={accent ? { borderInlineStartColor: accent } : undefined}
      onClick={() => model.onOpen?.()}
      role={model.onOpen ? 'link' : undefined}
      tabIndex={model.onOpen ? 0 : undefined}
      onKeyDown={(e) => { if (model.onOpen && (e.key === 'Enter')) { e.preventDefault(); model.onOpen(); } }}
    >
      {/* Header */}
      <div className={styles.cardHeader}>
        {model.authorAvatarUrl ? (
          <img
            className={`${styles.avatar} ${model.onAuthor ? styles.clickableInline : ''}`}
            src={mediaSrc(model.authorAvatarUrl) ?? undefined}
            alt=""
            loading="lazy"
            onClick={(e) => { if (model.onAuthor) { e.stopPropagation(); model.onAuthor(); } }}
          />
        ) : (
          <div className={styles.avatarFallback} aria-hidden>{initials(model.authorName ?? '·')}</div>
        )}
        <span className={styles.headerLine}>
          <span
            className={model.onAuthor ? styles.clickableInline : undefined}
            onClick={(e) => { if (model.onAuthor) { e.stopPropagation(); model.onAuthor(); } }}
          >
            <span className={styles.authorName}>{model.authorName}</span>
          </span>
          {model.postedInName && (
            <>
              {' '}<span className={styles.postedIn}>·</span>{' '}
              <span
                className={`${styles.communityName} ${model.postedInHref ? styles.clickableInline : ''}`}
                onClick={(e) => { if (model.onCta) { e.stopPropagation(); model.onCta(); } }}
              >
                {model.postedInName}
              </span>
            </>
          )}
        </span>
        {model.dateText && <span className={styles.cardTime}>{model.dateText}</span>}
        {model.badgeText && <span className={styles.badge}>{model.badgeText}</span>}
        {menu.length > 0 && (
          <div className={styles.menuWrap} ref={menuOpen ? menuRef : undefined}>
            <button
              type="button"
              className={styles.menuTrigger}
              aria-label="Post options"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className={styles.menuDropdown}>
                {menu.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className={a.danger ? styles.menuItemDanger : styles.menuItem}
                    onClick={(e) => { e.stopPropagation(); setMenuOpen(false); a.onSelect(); }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media */}
      {model.embedHtml && model.embedType === 'video' ? (
        <div
          className={styles.embed}
          onClick={(e) => e.stopPropagation()}
          dangerouslySetInnerHTML={{ __html: sanitizeEmbed(model.embedHtml) }}
        />
      ) : model.imageSrc ? (
        <FeedImage
          className={`${styles.media} ${canTheater ? styles.mediaClickable : ''}`}
          src={model.imageSrc}
          onClick={onImageClick}
        />
      ) : null}

      {model.title && <div className={styles.cardTitle}>{model.title}</div>}
      {model.bodyHtml ? (
        <div className={styles.cardBody} dangerouslySetInnerHTML={{ __html: sanitizeRichText(model.bodyHtml) }} />
      ) : model.body ? (
        <p className={styles.cardBody}>{model.body}</p>
      ) : null}
      {model.sharerText && <div className={styles.sharer}>{model.sharerText}</div>}

      {model.ctaLabel && model.onCta && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cta}
            onClick={(e) => { e.stopPropagation(); model.onCta!(); }}
          >
            {model.ctaLabel}
          </button>
        </div>
      )}

      {enableComments && anchor && (
        <FeedActionBar
          anchorType={anchor.anchorType}
          anchorId={anchor.anchorId}
          post={{
            authorName: model.authorName || undefined,
            authorAvatarUrl: model.authorAvatarUrl,
            authorHref: model.authorHref,
            dateText: model.dateText || undefined,
            body: model.body,
            imageUrl: model.theaterImageUrl ?? model.imageSrc,
          }}
        />
      )}

      {canTheater && anchor && (
        <ConversationModal
          anchorType={anchor.anchorType}
          anchorId={anchor.anchorId}
          open={theaterOpen}
          layout="theater"
          onClose={() => setTheaterOpen(false)}
          post={{
            authorName: model.authorName || undefined,
            authorAvatarUrl: model.authorAvatarUrl,
            authorHref: model.authorHref,
            dateText: model.dateText || undefined,
            body: model.body,
            imageUrl: model.theaterImageUrl ?? model.imageSrc,
            linkUrl: model.linkUrl,
          }}
        />
      )}
    </div>
  );
};

export default FeedCard;
